<template>
	<layout-error-boundary
		ref="errBoundary"
		v-slot="{ hasError, err }"
		:on-error="handleFormError"
	>
		<v-card-text v-if="hasError">
			<v-alert
				type="error"
				border="left"
				dismissible
				outlined
				@click.native="$refs.errBoundary.resetError()"
			>
				<span class="error--text">
					{{ errorMessage(err) }}
				</span>
			</v-alert>
		</v-card-text>

		<v-form
			v-if="type"
			ref="form"
			autocomplete="off"
			lazy-validation
			@submit.prevent="submit"
		>
			<v-alert v-if="hasExtraSteps" type="warning" border="left" outlined>
				This scene has conditions that are not available in this wizard. Please
				be wary of making changes.
			</v-alert>

			<!-- wizard -->
			<Wizard
				:is="type"
				ref="wizard"
				:scene="scene"
				:form.sync="payload"
				:fields.sync="fields"
				@update:extra="hasExtraSteps = $event"
			/>

			<div v-if="editMode" class="d-flex flex justify-space-around">
				<v-btn width="40%" min-width="100px" outlined text @click="reset"
					>Cancel</v-btn
				>
				<v-btn
					width="40%"
					min-width="100px"
					outlined
					type="submit"
					color="ocean"
					>Save</v-btn
				>
			</div>
		</v-form>
		<v-alert v-else type="info" border="left" outlined>
			No wizard available for this scene.
		</v-alert>
	</layout-error-boundary>
</template>

<script>
import _ from "lodash";
import { APIError, APIAuthError } from "@/utils/http";
import { mapActions, mapGetters } from "vuex";

export default {
	components: {
		temperature: () => import("./Temperature.vue"),
		trigger: () => import("./Trigger.vue"),
	},

	props: {
		scene: {
			type: Object,
			requred: true,
		},
	},

	data() {
		return {
			type: null,
			payload: {},
			fields: [],
			hasExtraSteps: false,
		};
	},

	computed: {
		...mapGetters("scenes", ["editMode"]),
	},

	watch: {
		scene() {
			this.checkType();
		},

		"scene.name"() {
			this.payload = {};
			this.fields = [];
		},

		editMode(edit) {
			if (!edit) this.reset();
		},
	},

	created() {
		this.checkType();
	},

	methods: {
		...mapActions("scenes", ["checkWizard", "addWizard"]),

		updateFormData(payload) {
			this.payload = payload;
		},

		updateFormFields(fields) {
			this.fields = fields;
		},

		async checkType() {
			this.$emit("update:loading", true);
			// check wizard type
			let type = await this.checkWizard(this.scene);
			this.type = _.get(
				type.find((r) => r.match),
				"wizard",
				null
			);
			this.$emit("update:loading", false);
		},

		reset() {
			// TODO trigger dialog if changes should be discarded
			if (this.$refs.wizard) {
				this.$refs.wizard.reset();
			}
			this.$store.commit("scenes/setEditMode", false);
			this.$emit("cancel");
		},

		async submit() {
			try {
				this.$emit("update:loading", true);
				await this.addWizard({
					wizard: this.type,
					form: this.payload,
					fields: this.fields,
					edit: true,
					scene: this.scene,
				});
				this.reset();
				this.$emit("submit");
			} finally {
				this.$emit("update:loading", false);
			}
		},

		errorMessage(err) {
			const preamble = err.isDeleteError
				? this.strings.deleteError
				: this.strings.error;
			const message = err.message ? `: ${err.message}` : "";
			return `${preamble}${message}`;
		},

		handleFormError(err) {
			// We only want to handle certain types of errors. Keep propagating
			// any others.
			const isApiErr =
				err instanceof APIError && !(err instanceof APIAuthError);
			return !isApiErr;
		},
	},
};
</script>

<style lang="scss" scoped>
::v-deep .v-alert i.v-alert__icon {
	align-self: auto;
}
// ::v-deep .v-input--is-readonly > .v-input__control > .v-input__slot:before,
// ::v-deep .v-input--is-readonly > .v-input__control > .v-input__slot:after {
// 	border: none;
// }
</style>
